import {
	RiAddLine,
	RiCloseFill,
	RiSearchLine,
	RiUserAddLine,
} from '@remixicon/react';
import { useT } from '@transifex/react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useId } from 'react';
import type { ChangeEvent, ComponentProps, ReactNode, Ref } from 'react';

import { Button } from '@playbook/atoms/button';
import { NavigationClubSelector } from '@playbook/compositions/navigation-club-selector';
import { PopoutMenu } from '@playbook/modules/popout-menu';
import type {
	PopoutMenuAppearFrom,
	PopoutMenuHandle,
} from '@playbook/modules/popout-menu';

import css from './styles.module.css';

const ClubMenuTransition = { duration: 0.15, ease: 'easeOut' } as const;

const ClubMenuReducedTransition = { duration: 0 } as const;

const leadingActionMotion = {
	initial: { opacity: 0, x: -24 },
	animate: { opacity: 1, x: 0 },
	exit: { opacity: 0, x: -24 },
} as const;

type ClubMenuGroupEventHandlers = {
	[K in keyof ComponentProps<'div'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'div'>[K];
};

interface ClubMenuGroupDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

interface ClubMenuGroupProperties
	extends ClubMenuGroupEventHandlers, ClubMenuGroupDataAttributes {
	renderHeaderWith: ReactNode;

	children: ReactNode;
}

export function ClubMenuGroup({
	renderHeaderWith,

	children,

	...props
}: ClubMenuGroupProperties): ReactNode {
	return (
		<div {...props} className={css.group}>
			{renderHeaderWith}
			<div className={css.items}>{children}</div>
		</div>
	);
}

type ClubMenuEventHandlers = {
	[K in keyof ComponentProps<'div'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'div'>[K];
};

interface ClubMenuDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

interface ClubMenuProperties
	extends ClubMenuEventHandlers, ClubMenuDataAttributes {
	appearFrom?: PopoutMenuAppearFrom;

	label: string;
	description?: string;
	imageUrl?: string;
	alt?: string;

	search?: string;
	searchPlaceholder: string;
	clearSearchLabel?: string;
	joinLabel?: string;
	addLabel?: string;

	onSearchChange?: (search: string) => void;
	onJoin?: () => void;
	onAdd?: () => void;

	leadingAction?: ReactNode;
	children: ReactNode;

	ref?: Ref<PopoutMenuHandle>;
}

export function ClubMenu({
	appearFrom,

	label,
	description,
	imageUrl,
	alt,

	search,
	searchPlaceholder,
	clearSearchLabel,
	joinLabel,
	addLabel,

	onSearchChange,
	onJoin,
	onAdd,

	leadingAction,
	children,

	ref,

	...props
}: ClubMenuProperties): ReactNode {
	const t = useT();
	const clearSearch: string = clearSearchLabel ?? t('Clear search');
	const join: string = joinLabel ?? t('Join a new group or club');
	const add: string = addLabel ?? t('Add or manage children');

	const field: string = `${useId().replaceAll(':', '')}-search`;
	const transition = useReducedMotion()
		? ClubMenuReducedTransition
		: ClubMenuTransition;

	const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void =>
		onSearchChange?.(event.target.value);

	const handleSearchClear = (): void => onSearchChange?.('');

	return (
		<PopoutMenu
			{...props}
			ref={ref}
			appearFrom={appearFrom}
			renderTriggerWith={(popoverTarget) => (
				<NavigationClubSelector
					label={label}
					description={description}
					imageUrl={imageUrl}
					alt={alt}
					popoverTarget={popoverTarget}
				/>
			)}>
			<div className={css.menu}>
				<div className={css.search}>
					<AnimatePresence mode="popLayout" initial={false}>
						{leadingAction && (
							<motion.div
								key="leading-action"
								{...leadingActionMotion}
								transition={transition}
								className={css.leading}>
								{leadingAction}
							</motion.div>
						)}
					</AnimatePresence>
					<motion.label layout transition={transition} className={css.field}>
						<RiSearchLine />
						<input
							type="text"
							id={field}
							name={field}
							placeholder={searchPlaceholder}
							className={css.control}
							value={search}
							onChange={onSearchChange ? handleSearchChange : undefined}
						/>
						{search && (
							<button
								type="button"
								aria-label={clearSearch}
								className={css.clear}
								onClick={handleSearchClear}>
								<RiCloseFill />
							</button>
						)}
					</motion.label>
				</div>
				<div className={css.contents}>{children}</div>
				<div className={css.controls}>
					<Button label={join} leadingIcon={<RiAddLine />} onClick={onJoin} />
					<Button label={add} leadingIcon={<RiUserAddLine />} onClick={onAdd} />
				</div>
			</div>
		</PopoutMenu>
	);
}
